import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';

import { TooltipDirective } from '../tooltip/tooltip.directive';

/**
 * Data structure for the open shift card.
 * Mirrors OpenShiftUnit but avoids cross-library dependency.
 */
export interface OpenShiftCardData {
  /** Unique key for tracking (shiftUnitId + date) */
  id: string;
  /** Shift name for display */
  shiftName: string;
  /** Shift color for visual styling */
  shiftColor: string;
  /** Shift start time (HH:mm) */
  startTime: string;
  /** Shift end time (HH:mm) */
  endTime: string;
  /** The date this open slot is for (YYYY-MM-DD) */
  date: string;
  /** Formatted date label for display */
  dateLabel: string;
  /** Team name */
  teamName: string;
  /** Plan name */
  planName: string;
  /** Number of available slots */
  availableSlots: number;
  /** Total capacity */
  totalCapacity: number;
  /** Whether the employee is eligible to apply */
  eligible: boolean;
  /** Whether the employee already applied */
  alreadyApplied: boolean;
  /** Whether the employee is already assigned (by planner) */
  alreadyAssigned: boolean;
  /** Ineligibility reason keys for display */
  ineligibilityReasons: string[];

  /** Card type: 'application' (default), 'swap', or 'takeover'. */
  type?: 'application' | 'swap' | 'takeover';
  /** For swap/takeover: display name of the employee who holds this shift. */
  assignedEmployeeName?: string;
  /** Whether the current user already has a pending request for this shift. */
  hasPendingRequest?: boolean;
  /** If a pending request exists, the request ID (for cancel). */
  pendingRequestId?: string;
  /** The application ID (for withdraw on applications). */
  applicationId?: string;
}

/**
 * OpenShiftCard Component
 *
 * Displays an open shift unit in a card format for the collection layout.
 * Shows shift name, time, team, capacity, and eligibility state with an
 * apply action or status badge. Supports three card types:
 * - application (default): standard open shift with Apply button
 * - swap: employee-marked shift offering a swap (Request Swap button)
 * - takeover: employee-marked shift available for takeover (Request Takeover button)
 */
@Component({
  selector: 'zag-open-shift-card',
  templateUrl: './open-shift-card.html',
  styleUrls: ['./open-shift-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon, TranslatePipe, TooltipDirective],
  host: {
    '[class.zag-open-shift-card]': 'true',
  },
})
export class OpenShiftCard {
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  /** The open shift card data to display */
  readonly shift = input.required<OpenShiftCardData>();

  /** Whether the apply action is in progress */
  readonly applying = input(false);

  /** Emitted when the user clicks the Apply button (application type) */
  readonly applyClick = output<OpenShiftCardData>();

  /** Emitted when the user clicks the Request Swap button (swap type) */
  readonly swapClick = output<OpenShiftCardData>();

  /** Emitted when the user clicks the Request Takeover button (takeover type) */
  readonly takeoverClick = output<OpenShiftCardData>();

  /** Emitted when the user clicks Cancel on a pending request */
  readonly cancelClick = output<OpenShiftCardData>();

  /** Whether this is a swap or takeover card */
  protected readonly isSwapType = computed(() => {
    const type = this.shift().type;
    return type === 'swap' || type === 'takeover';
  });

  /**
   * Card is interactive (clickable) when the user can take a primary action.
   * Not interactive when already applied/assigned or ineligible.
   */
  protected readonly cardVariant = computed((): 'interactive' | 'elevated' => {
    const s = this.shift();
    if (s.alreadyApplied || s.alreadyAssigned) return 'elevated';
    if (!s.eligible) return 'elevated';
    if (s.hasPendingRequest) return 'elevated';
    return 'interactive';
  });

  /** Tooltip text for the "Not Eligible" badge */
  protected readonly ineligibilityTooltip = computed(() => {
    this.currentLang();
    const reasons = this.shift().ineligibilityReasons;
    if (reasons.length === 0) return '';
    return reasons.map(r => this.translate.instant(`shiftApplications.reasons.${r}`)).join('\n');
  });

  /** Card click fires the primary action based on card type */
  onCardClick(): void {
    const s = this.shift();
    if (s.alreadyApplied || s.alreadyAssigned || !s.eligible || s.hasPendingRequest) return;
    if (s.type === 'swap') {
      this.swapClick.emit(s);
    } else if (s.type === 'takeover') {
      this.takeoverClick.emit(s);
    } else {
      this.applyClick.emit(s);
    }
  }

  // Keep existing event emitters for backward compatibility — still usable if
  // a consumer wants to wire them directly rather than relying on card click.
  onApply(event: Event): void {
    event.stopPropagation();
    this.applyClick.emit(this.shift());
  }

  onSwap(event: Event): void {
    event.stopPropagation();
    this.swapClick.emit(this.shift());
  }

  onTakeover(event: Event): void {
    event.stopPropagation();
    this.takeoverClick.emit(this.shift());
  }

  onCancel(event: Event): void {
    event.stopPropagation();
    this.cancelClick.emit(this.shift());
  }
}
